import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useUpdateProduct } from "../../hooks/useUpdateProduct"
import type { ProductLocationAddFormValues } from "../../schemas/productFormSchema"
import { ProductLocationAddFormSchema } from "../../schemas/productFormSchema"
import ProductShipLocationSelect, { type LocationAssociation } from "../ProductShipLocationSelect"

import { Button } from "@/components/common/Button"
import type { Product } from "@/graphql/codegen/graphql"

interface AddProductLocationDialogProps {
  product: Product
  onClose: () => void
  onSubmit: () => void
}

const AddProductLocationDialog: FC<AddProductLocationDialogProps> = ({ product, onClose, onSubmit }) => {
  const methods = useForm<ProductLocationAddFormValues>({
    defaultValues: {
      companyId: product.companyId ?? "",
      locationsAssociations: [],
    },
    resolver: zodResolver(ProductLocationAddFormSchema),
    mode: "all",
  })
  const {
    formState: { isDirty, isValid, isSubmitting },
    control,
    handleSubmit,
  } = methods
  const canSubmit = isDirty && isValid && !isSubmitting
  const { update } = useUpdateProduct({ onSuccess: onSubmit })

  const submitForm = async (formValues: ProductLocationAddFormValues) => {
    await update({
      productId: product.productId,
      locationsAssociations: (formValues.locationsAssociations as LocationAssociation[]).map(
        ({ locationAssociationId }) => ({
          locationAssociationId,
        })
      ),
    })
  }

  return (
    <Dialog open onClose={onClose} fullWidth>
      <DialogTitle className='pl-10 font-normal text-primary'>Add Location</DialogTitle>
      <IconButton aria-label='close' onClick={onClose} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <ProductShipLocationSelect name='locationsAssociations' control={control} />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} onClick={handleSubmit(submitForm)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddProductLocationDialog
